<template>
  <div dir="rtl" class="mr-8 px-4 sm:px-6 lg:px-8">
    <!-- Alert -->
    <div
      v-if="alert.show"
      :class="[
        'mb-6 p-4 rounded-md border',
        alert.type === 'success' ? 'bg-green-50 border-green-200 text-green-800' : '',
        alert.type === 'danger' ? 'bg-red-50 border-red-200 text-red-800' : '',
        alert.type === 'warning' ? 'bg-yellow-50 border-yellow-200 text-yellow-800' : ''
      ]"
    >
      {{ alert.message }}
    </div>

    <!-- Page Header -->
    <div class="sm:flex sm:items-center">
      <div class="sm:flex-auto">
        <h1 class="text-xl font-semibold text-gray-900">المكالمات الأصلية لكل فرع</h1>
        <p class="mt-2 text-sm text-gray-700">
          عند التفعيل تصل مكالمة المعلم إلى الدارس كمكالمة حقيقية في نظام الجهاز
          (شاشة اتصال على الشاشة المقفلة مع زر رد ورفض) بدلًا من إشعار عادي.
        </p>
      </div>
    </div>

    <!-- The operational facts an operator needs before flipping anything -->
    <div class="mt-4 p-4 rounded-md border bg-yellow-50 border-yellow-200 text-yellow-800 text-sm">
      <strong>قبل التفعيل:</strong>
      التفعيل لا يؤثر إلا على الأجهزة التي تعمل بالإصدار الجديد من التطبيق؛
      الإصدارات القديمة تتجاهل الإعداد وتستمر بالإشعار المعتاد.
      يُنصح بتفعيل Android أولًا لأنه يعمل عبر إشعارات FCM الحالية،
      أما iOS فيحتاج إصدارًا موقّعًا للنشر.
      <br />
      <strong>للتراجع الفوري:</strong>
      أطفئ المفتاح — المكالمة التالية ترجع مباشرة إلى الإشعار المعتاد
      دون الحاجة إلى تحديث التطبيق أو إعادة النشر.
    </div>

    <!-- Search + filter -->
    <div class="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
      <div class="relative">
        <input
          v-model="searchTerm"
          type="text"
          placeholder="البحث في الفروع..."
          class="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm pr-3"
        />
      </div>
      <div class="relative">
        <select
          v-model="filterStatus"
          class="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
        >
          <option value="all">كل الفروع</option>
          <option value="enabled">مُفعّل (أي منصة)</option>
          <option value="off">مُعطّل</option>
        </select>
      </div>
      <div class="text-sm text-gray-600 bg-blue-50 px-3 py-2 rounded-lg flex items-center">
        إجمالي الفروع: {{ rows.length }}
      </div>
      <div class="text-sm text-gray-600 bg-green-50 px-3 py-2 rounded-lg flex items-center">
        المُفعّلة: {{ enabledCount }}
      </div>
    </div>

    <!-- Table -->
    <div class="mt-8 flex flex-col">
      <div v-if="loading" class="text-center py-12">
        <div class="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <p class="mt-2 text-gray-600">جاري التحميل...</p>
      </div>

      <div v-else-if="filteredRows.length === 0" class="text-center py-12">
        <p class="text-gray-600">لا توجد فروع مطابقة.</p>
      </div>

      <div v-else class="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
        <div class="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
          <div class="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
            <table class="min-w-full divide-y divide-customHafadhColor">
              <thead class="bg-customHafadhColor text-center">
                <tr>
                  <th class="py-3.5 pr-4 pl-3 text-right text-sm font-semibold text-gray-900 sm:pr-6">#</th>
                  <th class="px-3 py-3.5 text-right text-sm font-semibold text-gray-900">اسم الفرع</th>
                  <th class="px-3 py-3.5 text-right text-sm font-semibold text-gray-900">Android</th>
                  <th class="px-3 py-3.5 text-right text-sm font-semibold text-gray-900">iOS</th>
                  <th class="px-3 py-3.5 text-right text-sm font-semibold text-gray-900">مدة الرنين</th>
                  <th class="px-3 py-3.5 text-right text-sm font-semibold text-gray-900">أقل إصدار</th>
                  <th class="px-3 py-3.5 text-right text-sm font-semibold text-gray-900">إجراءات</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-200 bg-white">
                <tr v-for="(row, index) in filteredRows" :key="row.tenant.id">
                  <td class="whitespace-nowrap py-4 pr-4 pl-3 text-sm text-gray-900 sm:pr-6">{{ index + 1 }}</td>
                  <td class="whitespace-nowrap px-3 py-4 text-sm font-medium text-gray-900">
                    {{ row.tenant.name }}
                    <div class="text-xs text-gray-500">{{ row.tenant.code || row.tenant.id }}</div>
                  </td>
                  <td class="whitespace-nowrap px-3 py-4 text-sm">
                    <span :class="badgeClass(row.config?.NATIVE_CALL_ANDROID_ENABLED)">
                      {{ parseBool(row.config?.NATIVE_CALL_ANDROID_ENABLED) ? 'مُفعّل' : 'مُعطّل' }}
                    </span>
                  </td>
                  <td class="whitespace-nowrap px-3 py-4 text-sm">
                    <span :class="badgeClass(row.config?.NATIVE_CALL_IOS_ENABLED)">
                      {{ parseBool(row.config?.NATIVE_CALL_IOS_ENABLED) ? 'مُفعّل' : 'مُعطّل' }}
                    </span>
                  </td>
                  <td class="whitespace-nowrap px-3 py-4 text-sm font-mono"
                      :class="row.config?.NATIVE_CALL_RING_SECONDS ? 'text-gray-900' : 'text-gray-400'">
                    {{ row.config?.NATIVE_CALL_RING_SECONDS || '40 (افتراضي)' }}
                  </td>
                  <td class="whitespace-nowrap px-3 py-4 text-sm font-mono"
                      :class="row.config?.NATIVE_CALL_MIN_APP_VERSION ? 'text-gray-900' : 'text-gray-400'">
                    {{ row.config?.NATIVE_CALL_MIN_APP_VERSION || '1.5.0 (افتراضي)' }}
                  </td>
                  <td class="whitespace-nowrap py-4 pr-4 pl-3 text-sm font-medium sm:pr-6">
                    <button class="enabled-button" @click="openModal(row)">تعديل</button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <!-- Edit modal -->
    <div v-if="showModal" class="fixed inset-0 z-50 overflow-y-auto" dir="rtl">
      <div class="flex items-center justify-center min-h-screen px-4">
        <div class="fixed inset-0 bg-gray-500 bg-opacity-75" @click="closeModal"></div>

        <div class="relative bg-white rounded-lg max-w-lg w-full p-6 shadow-xl">
          <h3 class="text-lg font-semibold text-gray-900">
            إعدادات المكالمات — {{ activeTenant?.name }}
          </h3>
          <p class="mt-1 text-xs text-gray-500">{{ activeTenant?.code || activeTenant?.id }}</p>

          <div class="mt-5 space-y-4">
            <label class="flex items-center gap-3">
              <input v-model="formData.NATIVE_CALL_ANDROID_ENABLED" type="checkbox" class="h-4 w-4" />
              <span class="text-sm text-gray-800">تفعيل المكالمات الأصلية على Android</span>
            </label>

            <label class="flex items-center gap-3">
              <input v-model="formData.NATIVE_CALL_IOS_ENABLED" type="checkbox" class="h-4 w-4" />
              <span class="text-sm text-gray-800">تفعيل المكالمات الأصلية على iOS</span>
            </label>

            <!-- iOS is the riskier switch: it also turns on PushKit token
                 registration on devices, which is not something to enable before
                 a signed build is actually out. -->
            <div
              v-if="formData.NATIVE_CALL_IOS_ENABLED && !parseBool(originalConfig?.NATIVE_CALL_IOS_ENABLED)"
              class="p-3 rounded-md border bg-yellow-50 border-yellow-200 text-yellow-800 text-xs"
            >
              تفعيل iOS يبدأ تسجيل رموز VoIP على الأجهزة. تأكد من نشر إصدار iOS
              الجديد أولًا؛ الأجهزة التي تعمل بإصدار أقدم من «أقل إصدار» تبقى على
              الإشعار المعتاد.
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700">مدة الرنين (ثانية)</label>
              <input
                v-model="formData.NATIVE_CALL_RING_SECONDS"
                type="number"
                min="15"
                max="120"
                placeholder="40"
                class="mt-1 block w-full py-2 px-3 border border-gray-300 rounded-md shadow-sm sm:text-sm"
              />
              <p class="mt-1 text-xs text-gray-500">بين 15 و 120. اتركه فارغًا للقيمة الافتراضية (40).</p>
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700">أقل إصدار للتطبيق</label>
              <input
                v-model="formData.NATIVE_CALL_MIN_APP_VERSION"
                type="text"
                placeholder="1.5.0"
                class="mt-1 block w-full py-2 px-3 border border-gray-300 rounded-md shadow-sm sm:text-sm"
              />
              <p class="mt-1 text-xs text-gray-500">
                الإصدارات الأقدم لا تستقبل مكالمات VoIP وتبقى على الإشعار المعتاد.
                اتركه فارغًا للقيمة الافتراضية (1.5.0).
              </p>
            </div>
          </div>

          <div class="mt-6 flex justify-end gap-3">
            <button class="disabled-button" :disabled="saving" @click="closeModal">إلغاء</button>
            <button class="enabled-button" :disabled="saving" @click="handleSave">
              {{ saving ? 'جاري الحفظ...' : 'حفظ' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent } from 'vue';
import {
  listNativeCallConfigs,
  upsertTenantNativeCallConfig,
} from '@/services/nativeCallConfigs.js';

// Stored as the literal strings '1'/'0' by the API — the mobile app and
// CallService both compare against '1'. Note the FORCE_UPDATE codes on the same
// table use 'true'/'false' instead; the two conventions are not interchangeable.
const parseBool = (value) => String(value ?? '').trim() === '1';

const emptyForm = () => ({
  NATIVE_CALL_ANDROID_ENABLED: false,
  NATIVE_CALL_IOS_ENABLED: false,
  NATIVE_CALL_RING_SECONDS: '',
  NATIVE_CALL_MIN_APP_VERSION: '',
});

export default defineComponent({
  name: 'TenantNativeCallConfigsManagement',
  data() {
    return {
      rows: [],
      loading: false,
      saving: false,
      searchTerm: '',
      filterStatus: 'all',
      showModal: false,
      activeTenant: null,
      originalConfig: null,
      formData: emptyForm(),
      alert: { show: false, type: 'success', message: '' },
    };
  },
  computed: {
    filteredRows() {
      let rows = this.rows;

      if (this.searchTerm) {
        const term = this.searchTerm.toLowerCase();
        rows = rows.filter((row) =>
          row.tenant.name?.toLowerCase().includes(term) ||
          row.tenant.code?.toLowerCase().includes(term),
        );
      }

      if (this.filterStatus !== 'all') {
        rows = rows.filter((row) => {
          const enabled = this.isRowEnabled(row);
          return this.filterStatus === 'enabled' ? enabled : !enabled;
        });
      }

      return rows;
    },
    enabledCount() {
      return this.rows.filter((row) => this.isRowEnabled(row)).length;
    },
  },
  mounted() {
    this.loadAll();
  },
  methods: {
    parseBool,
    isRowEnabled(row) {
      return parseBool(row.config?.NATIVE_CALL_ANDROID_ENABLED)
        || parseBool(row.config?.NATIVE_CALL_IOS_ENABLED);
    },
    async loadAll() {
      this.loading = true;
      try {
        this.rows = await listNativeCallConfigs();
      } catch (err) {
        console.error('Failed to load native call configs', err);
        this.showAlert('danger', err.response?.data?.result?.mesAr || 'فشل في تحميل البيانات');
      } finally {
        this.loading = false;
      }
    },
    badgeClass(value) {
      return parseBool(value)
        ? 'px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800'
        : 'px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600';
    },
    openModal(row) {
      this.activeTenant = row.tenant;
      this.originalConfig = row.config || {};
      // Not secrets, so prefill: an operator has to see what a branch is
      // currently on before changing it.
      this.formData = {
        NATIVE_CALL_ANDROID_ENABLED: parseBool(row.config?.NATIVE_CALL_ANDROID_ENABLED),
        NATIVE_CALL_IOS_ENABLED: parseBool(row.config?.NATIVE_CALL_IOS_ENABLED),
        NATIVE_CALL_RING_SECONDS: row.config?.NATIVE_CALL_RING_SECONDS || '',
        NATIVE_CALL_MIN_APP_VERSION: row.config?.NATIVE_CALL_MIN_APP_VERSION || '',
      };
      this.showModal = true;
    },
    closeModal() {
      this.showModal = false;
      this.activeTenant = null;
      this.originalConfig = null;
      this.formData = emptyForm();
    },
    async handleSave() {
      if (!this.activeTenant) return;
      this.saving = true;

      // The form is prefilled, so every field is sent. An empty text field is
      // sent as null, which deletes the row and restores the default.
      const payload = {
        NATIVE_CALL_ANDROID_ENABLED: this.formData.NATIVE_CALL_ANDROID_ENABLED,
        NATIVE_CALL_IOS_ENABLED: this.formData.NATIVE_CALL_IOS_ENABLED,
        NATIVE_CALL_RING_SECONDS: this.formData.NATIVE_CALL_RING_SECONDS === ''
          ? null
          : Number(this.formData.NATIVE_CALL_RING_SECONDS),
        NATIVE_CALL_MIN_APP_VERSION: this.formData.NATIVE_CALL_MIN_APP_VERSION.trim() || null,
      };

      try {
        await upsertTenantNativeCallConfig(this.activeTenant.id, payload);
        this.showAlert('success', 'تم حفظ إعدادات المكالمات');
        this.closeModal();
        await this.loadAll();
      } catch (err) {
        console.error('Failed to save native call config', err);
        this.showAlert('danger', err.response?.data?.result?.mesAr || 'فشل في حفظ الإعدادات');
      } finally {
        this.saving = false;
      }
    },
    showAlert(type, message) {
      this.alert = { show: true, type, message };
      setTimeout(() => {
        this.alert.show = false;
      }, 5000);
    },
  },
});
</script>
